/**
 * Certification approvals — both stages, for whoever holds them.
 *
 * ── Two queues, in order ──────────────────────────────────────────────────────────────────
 *
 *   Employee submits → Manager Approval → Certification Admin Approval → Approved / Rejected
 *
 * A manager sees the claims of their own direct reports that are waiting on them. The
 * certification admin — the employee named in Settings, or any existing system admin — sees the
 * claims the manager has already approved.
 *
 * The ordering is enforced in the SERVICE, not just by which buttons render: decide() refuses an
 * admin verdict on a claim the manager has not approved. Hiding a control is a courtesy; the
 * refusal is the rule.
 *
 * ── Nothing here belongs to the Expenses module ───────────────────────────────────────────
 *
 * Claims live in their own table with their own two approval fields. This page reads and writes
 * only those, so a change to certification approval cannot affect a travel claim and vice versa.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types/user';
import { UserRole as Role } from '../../types/user';
import { canonicalizeEmail, sameIdentity } from '../../services/identity';
import { CertificationResult } from '../../types/certification';
import {
  CertificationReimbursement,
  ReimbursementApprovalStatus,
  ReimbursementStatus,
  financeStatusOf,
} from '../../types/certificationReimbursement';
import {
  CertificationReimbursementService,
} from '../../services/certificationReimbursementService';
import { describeCertificationError } from '../../services/certificationService';
import { getCertificateUrl } from '../../services/certificationDocuments';
import { useCertificationApproval } from '../../hooks/useCertificationApproval';
import ApprovalStages from './ApprovalStages';
import CertificateViewerModal, { ViewingCertificate } from './CertificateViewerModal';
import './Certifications.css';

const formatDate = (iso?: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!match) return '—';
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${Number(match[3])}-${MONTHS[Number(match[2]) - 1]}-${match[1]}`;
};

const money = (amount: number, currency: string) =>
  `${currency === 'INR' ? '₹' : `${currency} `}${Number(amount || 0).toLocaleString()}`;

type Stage = 'manager' | 'admin' | 'finance';

/**
 * One queue, mounted three times.
 *
 * ── Why a prop rather than three pages ────────────────────────────────────────────────────
 *
 * Each approver wanted their own place to work, and three copies of this file would have meant
 * three copies of the claim card, the decide handler and the ordering rules — and the copy that
 * drifted would be the one that let a claim skip a stage. The rendering and the rules live here
 * once; the routes differ only in which stage they ask for.
 */
export interface CertificationApprovalsProps {
  stage: Stage;
}

const STAGE_COPY: Record<Stage, { title: string; lead: string; approve: string; empty: string }> = {
  manager: {
    title: 'Certification Approvals',
    lead: 'Claims from your direct reports. Approving sends them to the certification admin.',
    approve: 'Approve & send to admin',
    empty: 'Claims from your direct reports appear here as soon as they are submitted.',
  },
  admin: {
    title: 'Certificate Admin Approval',
    lead: 'Claims the manager has approved. Approving sends them to Finance for payment.',
    approve: 'Approve & send to Finance',
    empty: 'Claims appear here once the employee\u2019s manager has approved them.',
  },
  finance: {
    title: 'Finance \u00b7 Certification Reimbursements',
    lead:
      'Claims approved by both the manager and the certification admin, waiting to be paid. ' +
      'These are certification claims only \u2014 employee expenses are in the EMS Finance queue.',
    approve: 'Mark as Reimbursed',
    empty: 'Claims appear here once the certification admin has approved them.',
  },
};

const CertificationApprovals: React.FC<CertificationApprovalsProps> = ({ stage }) => {
  const { user } = useAuth();
  const {
    canApprove: canApproveAsAdmin,
    canViewManagerQueue,
    canViewAdminQueue,
    canViewFinanceQueue,
    via,
    ready,
  } = useCertificationApproval();
  const role = (user?.role as UserRole) || UserRole.EMPLOYEE;
  const myEmail = canonicalizeEmail(user?.email);
  const copy = STAGE_COPY[stage];

  /*
   * The SAME gates the sidebar uses, so a link can never lead to a page that refuses. Read from
   * the hook rather than recomputed here — two copies of a permission rule is how a menu and a
   * page come to disagree about who may do what.
   */
  const mayWorkQueue =
    stage === 'manager'
      ? canViewManagerQueue
      : stage === 'admin'
      ? canViewAdminQueue
      : canViewFinanceQueue;

  /*
   * HR and Super Admin hold the manager queue for OVERSIGHT, not because anybody reports to them.
   * Filtering it by reporting line would hand them a permanently empty page, so they see every
   * claim awaiting a manager. A manager still sees only their own reports' claims — that filter
   * is the reporting relationship itself, never a list of names.
   */
  const seesEveryManagerQueue = role === Role.HR_ADMIN || role === Role.SUPER_ADMIN;

  const [claims, setClaims] = useState<CertificationReimbursement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, string>>({});
  /* The certificate currently on screen, or null. See CertificateViewerModal. */
  const [viewing, setViewing] = useState<ViewingCertificate | null>(null);

  const load = useCallback(async () => {
    if (!ready) return;
    setLoading(true);
    setError('');
    try {
      setClaims(await CertificationReimbursementService.listAll());
    } catch (err: any) {
      setError(describeCertificationError(err, 'Could not load certification reimbursements.'));
    } finally {
      setLoading(false);
    }
  }, [ready]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * The claims this stage is waiting on.
   *
   * Every stage requires the one BEFORE it to have approved, so a claim can only ever appear in
   * one queue. That is what makes the flow sequential rather than three independent verdicts
   * that happen to be displayed in an order — and the Lambda enforces the identical rule, so a
   * claim cannot be advanced by calling the mutation directly either.
   *
   * The manager queue is matched with sameIdentity, not a raw string: one person is addressed on
   * two interchangeable domains here, and a native and a federated account carry different ones.
   */
  const queue = useMemo(() => {
    if (stage === 'manager') {
      return claims.filter(
        (c) =>
          c.managerApprovalStatus === ReimbursementApprovalStatus.PENDING &&
          (seesEveryManagerQueue || sameIdentity(c.managerEmail, myEmail))
      );
    }
    if (stage === 'admin') {
      return claims.filter(
        (c) =>
          c.managerApprovalStatus === ReimbursementApprovalStatus.APPROVED &&
          c.adminApprovalStatus === ReimbursementApprovalStatus.PENDING
      );
    }
    return claims.filter(
      (c) =>
        c.managerApprovalStatus === ReimbursementApprovalStatus.APPROVED &&
        c.adminApprovalStatus === ReimbursementApprovalStatus.APPROVED &&
        financeStatusOf(c) === ReimbursementApprovalStatus.PENDING
    );
  }, [claims, myEmail, stage, seesEveryManagerQueue]);

  /** Settled: rejected at any stage, or reimbursed. Shown for reference, never actionable. */
  const closed = useMemo(
    () =>
      claims.filter(
        (c) =>
          c.status === ReimbursementStatus.REJECTED ||
          c.status === ReimbursementStatus.REIMBURSED
      ),
    [claims]
  );

  const decide = async (claim: CertificationReimbursement, stage: Stage, approve: boolean) => {
    const reason = (comments[claim.id] || '').trim();
    setError('');
    setBusyId(claim.id);
    try {
      await CertificationReimbursementService.decide(claim, stage, approve, myEmail, reason);
      setNotice(
        approve
          ? `${claim.certificationName} approved${stage === 'manager' ? ' and sent to the certification admin' : ''}.`
          : `${claim.certificationName} rejected.`
      );
      setComments((prev) => ({ ...prev, [claim.id]: '' }));
      await load();
    } catch (err: any) {
      /*
       * describeCertificationError, not err.message.
       *
       * A refusal raised by the Lambda arrives as a GraphQL error — Amplify puts it in
       * `errors[0].message` and leaves the top-level `message` undefined. Reading `.message`
       * therefore fell through to the generic fallback and threw the server's actual sentence
       * away: "You cannot decide your own reimbursement claim." became "The decision could not
       * be recorded", which says nothing the approver can act on. The load path already used
       * this helper; the decision path is where the message matters most.
       */
      setError(describeCertificationError(err, 'The decision could not be recorded.'));
    } finally {
      setBusyId(null);
    }
  };

  const openDocument = async (claim: CertificationReimbursement) => {
    try {
      const url = await getCertificateUrl(
        claim.documentKey,
        claim.documentName || claim.certificationName
      );
      /* In a dialog over the queue, so an approver keeps their place in it. See
         CertificateViewerModal for why this is not a tab. */
      setViewing({
        url,
        title: claim.certificationName,
        documentName: claim.documentName || undefined,
      });
    } catch (err: any) {
      setError(err?.message || 'The document could not be opened.');
    }
  };

  if (!ready) return <div className="certs"><div className="certs__skeleton" /></div>;

  if (!mayWorkQueue) {
    return (
      <div className="certs">
        <div className="certs__empty">
          <h3>You do not have certification approval access.</h3>
          <p>
            Certification reimbursements are decided by the employee&rsquo;s manager, then by an
            administrator or the employee named in Settings &rsaquo; Certifications.
          </p>
        </div>
      </div>
    );
  }

  const renderClaim = (claim: CertificationReimbursement, stage: Stage | 'none') => (
    <div className="cert-approval" key={claim.id}>
      <div className="cert-approval__head">
        <div>
          <h3>{claim.certificationName}</h3>
          <p className="certs__muted">
            {claim.employeeName || claim.employeeEmail} ·{' '}
            {money(claim.reimbursementAmount, claim.currency)}
          </p>
        </div>
        <span className="certs__tag">{claim.status}</span>
      </div>

      <ApprovalStages claim={claim} />

      <div className="cert-approval__detail">
        <div><span>Provider</span><strong>{claim.provider}</strong></div>
        <div><span>Exam date</span><strong>{formatDate(claim.examDate)}</strong></div>
        <div><span>Result date</span><strong>{formatDate(claim.resultDate)}</strong></div>
        <div>
          <span>Result</span>
          <strong>
            <span
              className={`certs__pill certs__pill--${
                claim.result === CertificationResult.PASSED ? 'passed' : 'failed'
              }`}
            >
              {claim.result}
            </span>
          </strong>
        </div>
        {claim.retakeDeadline && (
          <div><span>Retake deadline</span><strong>{formatDate(claim.retakeDeadline)}</strong></div>
        )}
        <div>
          <span>Document</span>
          <strong>
            {claim.documentKey ? (
              <button type="button" className="certs__link" onClick={() => openDocument(claim)}>
                View
              </button>
            ) : (
              '—'
            )}
          </strong>
        </div>
        {claim.notes && <div><span>Employee note</span><strong>{claim.notes}</strong></div>}
      </div>

      {/*
        * A failed certification is still reimbursable.
        *
        * Said explicitly because the two words look alike on screen and mean opposite things —
        * an approver seeing "Failed" could easily read it as a decision already made.
        */}
      {claim.result === CertificationResult.FAILED && (
        <div className="cert-approval__note">
          The employee did not pass the exam. That is the exam result, not a decision about this
          claim — a failed attempt can still be reimbursed if your policy allows it.
        </div>
      )}

      {stage !== 'none' && (
        <div className="cert-approval__actions">
          <input
            type="text"
            placeholder="Comment (required to reject)"
            value={comments[claim.id] || ''}
            onChange={(e) => setComments((prev) => ({ ...prev, [claim.id]: e.target.value }))}
            disabled={busyId === claim.id}
          />
          <button
            type="button"
            className="certs__btn certs__btn--primary"
            disabled={busyId === claim.id}
            onClick={() => decide(claim, stage, true)}
          >
            {busyId === claim.id ? 'Saving…' : copy.approve}
          </button>
          <button
            type="button"
            className="certs__btn"
            disabled={busyId === claim.id}
            onClick={() => decide(claim, stage, false)}
          >
            Reject
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="certs">
      <div className="certs__head">
        <div>
          <h1>{copy.title}</h1>
          <p>
            {copy.lead}
            {stage === 'admin' && via === 'configured' &&
              ' You have this access because you are the configured Certification Admin \u2014 it covers certifications only.'}
          </p>
        </div>
        <button type="button" className="certs__btn" onClick={load} disabled={loading}>
          {loading ? 'Loading\u2026' : 'Refresh'}
        </button>
      </div>

      {error && <div className="certs__notice certs__notice--error" role="alert">{error}</div>}
      {notice && <div className="certs__notice" role="status">{notice}</div>}

      {loading && claims.length === 0 ? (
        <div className="certs__skeleton" />
      ) : (
        <>
          <h2 className="certs__section">Waiting on you ({queue.length})</h2>
          {queue.length === 0 ? (
            <div className="certs__empty">
              <h3>Nothing waiting on you.</h3>
              <p>{copy.empty}</p>
            </div>
          ) : (
            queue.map((claim) => renderClaim(claim, stage))
          )}

          {/*
            * Settled claims, for reference only — rendered with stage 'none' so no queue offers
            * a decision on something already decided. A reimbursed claim leaves every pending
            * queue and appears here, which is where its history is read afterwards.
            */}
          {closed.length > 0 && (
            <>
              <h2 className="certs__section">Closed ({closed.length})</h2>
              {closed.map((claim) => renderClaim(claim, 'none'))}
            </>
          )}
        </>
      )}

      {viewing && <CertificateViewerModal {...viewing} onClose={() => setViewing(null)} />}
    </div>
  );
};

export default CertificationApprovals;
